(() => {
  const initRail = (rail) => {
    const section = rail.closest('section');
    const previous = section?.querySelector('[data-p4-prev]');
    const next = section?.querySelector('[data-p4-next]');
    const pager = section?.querySelector('[data-p4-pager]');
    const cards = Array.from(rail.children);

    const step = () => {
      const first = cards[0];
      if (!first) return rail.clientWidth;
      const styles = getComputedStyle(rail);
      return first.getBoundingClientRect().width + (parseFloat(styles.gap || styles.columnGap) || 0);
    };

    previous?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));

    if (!pager || !cards.length) return;
    cards.forEach((card, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = String(index + 1);
      button.setAttribute('aria-label', `Show product ${index + 1}`);
      if (index === 0) button.classList.add('p4-pagerActive');
      button.addEventListener('click', () => rail.scrollTo({ left: card.offsetLeft - rail.offsetLeft, behavior: 'smooth' }));
      pager.append(button);
    });

    let frame = 0;
    rail.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const active = cards.reduce((closest, card, index) => {
          const distance = Math.abs(card.offsetLeft - rail.offsetLeft - rail.scrollLeft);
          const previousDistance = Math.abs(cards[closest].offsetLeft - rail.offsetLeft - rail.scrollLeft);
          return distance < previousDistance ? index : closest;
        }, 0);
        pager.querySelectorAll('button').forEach((button, index) => button.classList.toggle('p4-pagerActive', index === active));
      });
    }, { passive: true });
  };

  const initHome = (root) => {
    if (root.dataset.p4Ready === 'true') return;
    root.dataset.p4Ready = 'true';

    const revealItems = Array.from(root.querySelectorAll('[data-p4-reveal]'));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('p4-revealVisible'));
    } else {
      root.querySelectorAll('.p4-best + .p4-worlds, .p4-newArrivals + .p4-fieldNotes').forEach((item) => {
        item.dataset.p4JoinedTop = '';
        item.previousElementSibling.dataset.p4JoinedBottom = '';
      });
      revealItems.forEach((item) => {
        if (item.parentElement.classList.contains('p4-revealBackdrop')) return;
        const backdrop = document.createElement('div');
        backdrop.className = 'p4-revealBackdrop';
        let surface = item;
        let color = 'transparent';
        while (surface && (color === 'transparent' || color === 'rgba(0, 0, 0, 0)')) {
          color = getComputedStyle(surface).backgroundColor;
          surface = surface.parentElement;
        }
        backdrop.style.backgroundColor = color;
        item.before(backdrop);
        backdrop.append(item);
      });
      root.classList.add('p4-motionReady');
      const scrollRoot = root.closest('.page-wrapper');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('p4-revealVisible');
          observer.unobserve(entry.target);
        });
      }, {
        root: scrollRoot instanceof Element && scrollRoot.scrollHeight > scrollRoot.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(scrollRoot).overflowY) ? scrollRoot : null,
        threshold: 0.08,
        rootMargin: '0px 0px -7% 0px',
      });
      revealItems.forEach((item) => observer.observe(item));
    }

    const mobileMenu = root.querySelector('[data-p4-menu]');
    const setMenu = (open) => {
      mobileMenu?.classList.toggle('p4-open', open);
      mobileMenu?.setAttribute('aria-hidden', String(!open));
      document.documentElement.style.overflow = open ? 'hidden' : '';
    };
    root.querySelector('[data-p4-menu-open]')?.addEventListener('click', () => setMenu(true));
    root.querySelector('[data-p4-menu-close]')?.addEventListener('click', () => setMenu(false));
    mobileMenu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));

    const hero = root.querySelector('[data-p4-hero]');
    const slides = hero ? Array.from(hero.querySelectorAll('[data-p4-slide]')) : [];
    const dots = Array.from(root.querySelectorAll('[data-p4-hero-dots] button'));
    let activeSlide = 0;
    let heroTimer;
    const showSlide = (index) => {
      activeSlide = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === activeSlide;
        slide.hidden = !active;
        slide.classList.toggle('p4-isActive', active);
      });
      hero?.classList.toggle('p4-heroLightActive', slides[activeSlide]?.classList.contains('p4-heroLight'));
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeSlide;
        dot.classList.toggle('p4-heroDotActive', active);
        dot.setAttribute('aria-current', String(active));
      });
    };
    const startHero = () => {
      clearInterval(heroTimer);
      if (slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        heroTimer = setInterval(() => showSlide(activeSlide + 1), 7000);
      }
    };
    dots.forEach((dot, index) => dot.addEventListener('click', () => { showSlide(index); startHero(); }));
    hero?.addEventListener('mouseenter', () => clearInterval(heroTimer));
    hero?.addEventListener('mouseleave', startHero);
    hero?.addEventListener('focusin', () => clearInterval(heroTimer));
    hero?.addEventListener('focusout', (event) => {
      if (!hero.contains(event.relatedTarget)) startHero();
    });
    startHero();


    let heroTouch = null;
    let suppressHeroClickUntil = 0;
    hero?.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1 || slides.length < 2) {
        heroTouch = null;
        return;
      }
      const touch = event.touches[0];
      heroTouch = { x: touch.clientX, y: touch.clientY, horizontal: false };
      clearInterval(heroTimer);
    }, { passive: true });
    hero?.addEventListener('touchmove', (event) => {
      if (!heroTouch || event.touches.length !== 1) return;
      const dx = event.touches[0].clientX - heroTouch.x;
      const dy = event.touches[0].clientY - heroTouch.y;
      if (Math.abs(dy) > 12 && Math.abs(dy) >= Math.abs(dx)) {
        heroTouch = null;
        startHero();
        return;
      }
      if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        heroTouch.horizontal = true;
        if (event.cancelable) event.preventDefault();
      }
    }, { passive: false });
    hero?.addEventListener('touchend', (event) => {
      const gesture = heroTouch;
      heroTouch = null;
      if (gesture && event.changedTouches.length) {
        const touch = event.changedTouches[0];
        const dx = touch.clientX - gesture.x;
        const dy = touch.clientY - gesture.y;
        if (gesture.horizontal) suppressHeroClickUntil = Date.now() + 500;
        if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          showSlide(activeSlide + (dx < 0 ? 1 : -1));
          suppressHeroClickUntil = Date.now() + 500;
        }
      }
      startHero();
    }, { passive: true });
    hero?.addEventListener('touchcancel', () => {
      heroTouch = null;
      startHero();
    }, { passive: true });
    hero?.addEventListener('click', (event) => {
      if (Date.now() < suppressHeroClickUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    const floatingActions = Array.from(root.querySelectorAll('[data-p4-float]'));
    const scrollHost = root.closest('.page-wrapper') || window;
    const updateFloatingActions = () => {
      const visible = Boolean(hero && hero.getBoundingClientRect().bottom <= window.innerHeight * 0.35);
      floatingActions.forEach((action) => action.classList.toggle('p4-floatVisible', visible));
    };
    updateFloatingActions();
    scrollHost.addEventListener('scroll', updateFloatingActions, { passive: true });
    if (scrollHost !== window) window.addEventListener('scroll', updateFloatingActions, { passive: true });
    window.addEventListener('resize', updateFloatingActions);

    root.querySelectorAll('[data-p4-rail]').forEach(initRail);

    root.querySelector('[data-p4-chat]')?.addEventListener('click', () => {
      root.querySelector('#p4-faq')?.scrollIntoView({ behavior: 'smooth' });
    });

    root.querySelectorAll('[data-p4-add-form]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button');
        button?.setAttribute('aria-busy', 'true');
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form),
          });
          if (!response.ok) throw new Error('Unable to add product');
          const cart = await fetch('/cart.js').then((result) => result.json());
          root.querySelectorAll('[data-p4-cart-count]').forEach((count) => { count.textContent = String(cart.item_count); });
          document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'faunov-home' } }));
        } catch (_error) {
          form.submit();
        } finally {
          button?.removeAttribute('aria-busy');
        }
      });
    });

  };

  const initAll = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-p4-root]')) roots.push(scope);
    scope.querySelectorAll?.('[data-p4-root]').forEach((root) => roots.push(root));
    roots.forEach(initHome);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.('[data-p4-root]');
    if (root) initAll(root);
  });
})();
